/**
 * 公开合集列表（首页"合集" tab 使用）
 *
 * - 调用 `GET /api/collections`（不带 mine=1），后端只返回 visibility=public 的合集
 * - 无限滚动分页
 * - 所有用户看到完全一致的内容：和首页视频流一样的"统一窗口"
 *   即便用户自己拥有这些合集，这里也不显示编辑/删除按钮
 *   （我的合集管理请去"我的 -> 我的合集" tab）
 *
 * 受控筛选：
 * - searchKeyword: 父组件（首页 VideoList）已"确认"的搜索关键字；变化时回到第 1 页重新加载。
 * - refreshNonce:  父组件想要触发一次强制刷新时递增该值；同样回到第 1 页重新加载。
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { clearAuth, getAuthHeaders, getToken } from '../utils/auth';
import { useInfiniteObserver } from '../hooks/useInfiniteObserver';
import type { ApiResponse } from '../types/api';
import type { Collection, CollectionListResponseData } from '../types/collection';
import { CollectionCard } from './CollectionCard';
import ListStatusFooter from './common/ListStatusFooter';

const PAGE_SIZE = 20;

interface PublicCollectionListProps {
  /** 已"确认"的搜索关键字（点击放大镜 / 回车后），空串表示不过滤 */
  searchKeyword?: string;
  /** 父组件递增此值以触发一次强制刷新 */
  refreshNonce?: number;
  /** 未登录点击合集卡片时由父组件弹出登录提示 */
  onLoginRequired?: () => void;
  /** 为 true 时点击卡片不跳转详情 */
  requireLoginToOpen?: boolean;
}

const PublicCollectionList: React.FC<PublicCollectionListProps> = ({
  searchKeyword = '',
  refreshNonce = 0,
  onLoginRequired,
  requireLoginToOpen = false,
}) => {
  const navigate = useNavigate();

  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false);

  const loadPage = useCallback(
    async (page: number, append: boolean) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      if (append) setLoadingMore(true);
      else setLoading(true);

      try {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('pageSize', String(PAGE_SIZE));
        const kw = searchKeyword.trim();
        if (kw) params.set('search', kw);

        const res = await fetch(`${API_BASE_URL}/collections?${params.toString()}`, {
          headers: getAuthHeaders(),
        });
        if (res.status === 401) {
          const hadToken = getToken() !== null;
          clearAuth();
          if (hadToken) {
            navigate('/login');
            return;
          }
          setError('无法加载合集列表，请稍后重试');
          return;
        }
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = (await res.json()) as ApiResponse<CollectionListResponseData>;
        if (!data.success || !data.data) {
          throw new Error(data.error || '加载合集失败');
        }
        const list = data.data.list || [];
        setCollections((prev) => (append ? [...prev, ...list] : list));
        setHasMore(data.data.hasMore);
        setCurrentPage(page);
        setError(null);
      } catch (err) {
        console.error('[PublicCollectionList] 加载失败:', err);
        setError(err instanceof Error ? err.message : '加载失败');
      } finally {
        setLoading(false);
        setLoadingMore(false);
        loadingRef.current = false;
      }
    },
    [navigate, searchKeyword],
  );

  // 搜索关键字 / 刷新触发：回到第 1 页重新加载
  useEffect(() => {
    loadPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeyword, refreshNonce]);

  const onLoadMore = useCallback(() => {
    loadPage(currentPage + 1, true);
  }, [currentPage, loadPage]);

  const { sentinelRef } = useInfiniteObserver({
    enabled: true,
    hasMore,
    isLoading: loadingRef.current || loadingMore,
    onLoadMore,
    rootMargin: '100px',
    threshold: 0.1,
  });

  if (loading && collections.length === 0) {
    return <div className="loading">加载中...</div>;
  }

  if (error && collections.length === 0) {
    return <div className="error">错误: {error}</div>;
  }

  return (
    <>
      <div className="video-grid">
        {collections.map((c) => (
          <CollectionCard
            key={c.id}
            collection={c}
            // 首页是"统一窗口"：不区分是否为 owner，统一不显示编辑 / 删除按钮；
            // 同时此处只可能是 public 合集，"公开"徽章信息冗余，一并关闭。
            currentUserId={null}
            showVisibilityBadge={false}
            returnTo="/?tab=collections"
            requireLoginToOpen={requireLoginToOpen}
            onLoginRequired={onLoginRequired}
          />
        ))}
      </div>

      {collections.length === 0 && !loading && (
        <div className="empty-state">
          <p>{searchKeyword.trim() ? '没有找到匹配的合集' : '还没有任何公开合集'}</p>
        </div>
      )}

      <ListStatusFooter
        ref={sentinelRef}
        loadingMore={loadingMore}
        hasMore={hasMore}
        allowNoMore={collections.length > 0}
      />
    </>
  );
};

export default PublicCollectionList;
